import React, { useState, useEffect } from 'react';
import { Button, Card, CardBody, CardHeader, CardTitle, Col, Row, Badge, Collapse } from 'reactstrap';
import { Link, useNavigate } from "react-router-dom";
import axios from 'axios';
import { createApplication, autoCorrectApplicationStatus } from '../../helpers/Espo/application_helper'; // Assuming this helper function exists
import ApplicationPDFHandler from '../../components/PDFGenerator/ApplicationPDFHandler';
import {
    getStatusDisplay,
    getDisplayStatus,
    getStatusPermissions,
    APPLICATION_STATUS
} from '../../helpers/statusManager';

// Function to validate if all mandatory profile fields are complete
const validateProfileCompleteness = (applicant) => {
    const mandatoryFields = [
        'birthDate',
        'gender', 
        'phoneNumber',
        'aadharNumber',
        'addressStreet',
        'addressCity',
        'addressState',
        'addressPostalCode'
    ];
    
    if (!applicant) return false;
    
    // Check if all mandatory fields have values
    return mandatoryFields.every(field => {
        const value = applicant[field];
        // Check for null, undefined, empty string, or whitespace-only string
        return value !== null && value !== undefined && 
               typeof value === 'string' ? value.trim() !== '' : value !== '';
    });
};

// Function to extract and process all remarks from application
const processApplicationRemarks = (application) => {
    const remarkFields = [
        { key: 'piRemarksList', section: 'Personal Information', label: 'Personal Info' },
        { key: 'fiRemarksList', section: 'Family Information', label: 'Family Info' },
        { key: 'curRemarksList', section: 'Current Education', label: 'Current Education' },
        { key: 'pastRemarksList', section: 'Past Education', label: 'Past Education' },
        { key: 'cr1RemarksList', section: 'Community Reference 1', label: 'Reference 1' },
        { key: 'cr2RemarksList', section: 'Community Reference 2', label: 'Reference 2' }
    ];

    const processedRemarks = [];

    remarkFields.forEach(field => {
        const remarkValue = application[field.key];
        
        // Handle different data types
        let processedValue = '';
        
        if (remarkValue) {
            if (typeof remarkValue === 'string') {
                processedValue = remarkValue.trim();
            } else if (Array.isArray(remarkValue)) {
                // If it's an array, join the elements
                processedValue = remarkValue.filter(item => item && item.toString().trim()).join('; ');
            } else if (typeof remarkValue === 'object') {
                // If it's an object, try to extract meaningful text
                processedValue = JSON.stringify(remarkValue);
            } else {
                // Convert other types to string
                processedValue = remarkValue.toString().trim();
            }
        }
        
        if (processedValue && processedValue.length > 0) {
            processedRemarks.push({
                section: field.section,
                label: field.label,
                remark: processedValue
            });
        }
    });

    return processedRemarks;
};

// Component to display remarks
const RemarksDisplay = ({ remarks, applicationId }) => {
    const [isOpen, setIsOpen] = useState(false);

    if (remarks.length === 0) {
        return <span className="text-muted">No remarks</span>;
    }

    return (
        <div style={{ minWidth: '200px' }}>
            <Button
                color="warning"
                size="sm"
                outline
                onClick={() => setIsOpen(!isOpen)}
                className="me-1"
                style={{ 
                    borderWidth: '2px',
                    fontWeight: '600'
                }}
            >
                <i className="fas fa-exclamation-triangle me-1"></i>
                {remarks.length} Remark{remarks.length > 1 ? 's' : ''}
                <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} ms-1`}></i>
            </Button>
            <Collapse isOpen={isOpen}>
                <Card className="mt-2 border-warning" style={{ 
                    borderWidth: '2px',
                    backgroundColor: '#fff9e6',
                    boxShadow: '0 2px 8px rgba(255, 193, 7, 0.3)'
                }}>
                    <CardBody className="p-3">
                        <div className="mb-2">
                            <i className="fas fa-info-circle text-warning me-2"></i>
                            <strong className="text-warning">Important: Please review and address the following remarks</strong>
                        </div>
                        {remarks.map((remark, index) => (
                            <div key={index} className="mb-2">
                                <Badge 
                                    color="warning" 
                                    className="me-2"
                                    style={{ 
                                        fontSize: '0.75em',
                                        fontWeight: '600'
                                    }}
                                >
                                    {remark.label}
                                </Badge>
                                <div className="text-dark mt-1" style={{ 
                                    backgroundColor: '#ffffff',
                                    padding: '8px 12px',
                                    borderRadius: '4px',
                                    border: '1px solid #ffc107',
                                    fontSize: '0.9em',
                                    fontWeight: '500'
                                }}>
                                    {remark.remark}
                                </div>
                                {index < remarks.length - 1 && <hr className="my-2 border-warning" />}
                            </div>
                        ))}
                    </CardBody>
                </Card>
            </Collapse>
        </div>
    );
};

const handleCreateApplication = async (applicant) => {
    try {
        // Double-check: Fetch latest applications to ensure no 2025 application exists
        console.log("Checking for existing 2025 applications before creating new one...");
        const API_URL = process.env.REACT_APP_ESPO_API_URL+'/MMApplicationNew';
        const API_KEY = process.env.REACT_APP_ESPO_API_KEY;
        
        const existingAppsResponse = await axios.get(`${API_URL}?where[0][type]=equals&where[0][attribute]=mMApplicantId&where[0][value]=${applicant.id}`, {
            headers: {
                'x-api-key': API_KEY,
            }
        });

        if (existingAppsResponse && existingAppsResponse.total > 0) {
            const existingApps = existingAppsResponse.list;
            const has2025App = existingApps.some(app => 
                app.applicationNumber && app.applicationNumber.toString().startsWith('2025')
            );
            
            if (has2025App) {
                console.log("Found existing 2025 application, preventing duplicate creation");
                throw new Error("You already have a 2025/26 application. Only one application per year is allowed.");
            }
        }

        const newApplication = {
            mMApplicantId: applicant.id,  // Include any required fields
            status: APPLICATION_STATUS.DRAFT, // Use constant from status manager
            aidNeeded: 0,
            aidGranted: 0,
            aidNeededCurrency: 'INR',
            aidGrantedCurrency: 'INR',
            familysAnnualIncomeCurrency: 'INR',
            tuitionFeesCurrency: 'INR',
            lodgingFeesCurrency: 'INR',
            booksMaterialExpensesCurrency: 'INR',
            otherAidAmountCurrency: 'INR',
            // Any other necessary default values for a new application
        };

        console.log("Creating new application with data:", newApplication);
        const response = await createApplication(newApplication); // Assuming this returns the new application's data
        return response.id; // Return the ID of the created application
    } catch (error) {
        console.error("Error creating new application:", error);
        if (error.message.includes("already have a 2025")) {
            alert(error.message);
        } else {
            alert("Failed to create new application.");
        }
        return null;
    }
};

const ApplicationList = ({ applicant }) => { // Correct destructuring
    const navigate = useNavigate(); // Add React Router navigation hook
    const [applications, setApplications] = useState([]);
    const [existingApplication, setExistingApplication] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isProfileComplete, setIsProfileComplete] = useState(false);
    const [has2025Application, setHas2025Application] = useState(false);
    const [creatingApplication, setCreatingApplication] = useState(false); // Add loading state for button

    useEffect(() => {
        // Check profile completeness whenever applicant data changes
        if (applicant) {
            const profileComplete = validateProfileCompleteness(applicant);
            console.log('Profile completeness check:', {
                applicant: applicant,
                profileComplete: profileComplete,
                missingFields: Object.keys(applicant).filter(key => {
                    const mandatoryFields = [
                        'birthDate', 'gender', 'phoneNumber', 'aadharNumber',
                        'addressStreet', 'addressCity', 'addressState', 'addressPostalCode'
                    ];
                    if (mandatoryFields.includes(key)) {
                        const value = applicant[key];
                        return value === null || value === undefined || 
                               (typeof value === 'string' ? value.trim() === '' : value === '');
                    }
                    return false;
                })
            });
            setIsProfileComplete(profileComplete);
        }
    }, [applicant]);

    useEffect(() => {
        const fetchApplications = async () => {
            try {
                if (!applicant || !applicant.id) {
                    throw new Error("Applicant ID is missing.");
                }

                const url = `https://espo.2aana.com/api/v1/MMApplicant/${applicant.id}/mMApplicationNews`;
                const response = await axios.get(url, {
                    headers: {
                        'x-api-key': '3d464d1f4404b17969165f0eaeb45926',
                    }
                });

                console.log("Applications response:", response);

                if (response.list && Array.isArray(response.list) && response.total) {
                    // Auto-correct status for applications if needed and update UI immediately
                    const correctedApplications = await Promise.allSettled(
                        response.list.map(async (app) => {
                            try {
                                // Check and correct application status in background
                                const correctionResult = await autoCorrectApplicationStatus(app.id);
                                
                                if (correctionResult.corrected) {
                                    console.log(`Application ${app.id} status corrected from ${correctionResult.originalStatus} to ${correctionResult.newStatus}`);
                                    // Return app with corrected status for immediate UI update
                                    return {
                                        ...app,
                                        status: correctionResult.newStatus
                                    };
                                }
                                
                                return app; // Return original app if no correction needed
                            } catch (error) {
                                console.error(`Failed to auto-correct application ${app.id}:`, error);
                                return app; // Return original app if correction fails
                            }
                        })
                    );

                    // Extract successful results and any that failed
                    const applications = correctedApplications
                        .filter(result => result.status === 'fulfilled')
                        .map(result => result.value);

                    setApplications(applications);
                    setExistingApplication(true);
                    
                    // Check if any application number starts with "2025"
                    const has2025App = applications.some(app => 
                        app.applicationNumber && app.applicationNumber.toString().startsWith('2025')
                    );
                    setHas2025Application(has2025App);
                    console.log('2025 Application check:', { applications, has2025App });
                } else {
                    console.error("Unexpected response structure:", response);
                    setExistingApplication(false);
                    setApplications([]);
                    setHas2025Application(false);
                }
            } catch (error) {
                console.error("Error fetching applications data:", error);
                setError("Failed to load applications data");
            } finally {
                setLoading(false);
            }
        };

        if (applicant && applicant.id) {
            fetchApplications();
        }
    }, [applicant]);

    // Safe application creation handler with loading state
    const handleCreateNewApplication = async () => {
        // Prevent multiple clicks
        if (creatingApplication) {
            console.log("Application creation already in progress, ignoring click");
            return;
        }

        // Double check that user doesn't already have a 2025 application
        if (has2025Application) {
            alert("You already have a 2025/26 application. Only one application per year is allowed.");
            return;
        }

        try {
            setCreatingApplication(true);
            console.log("Creating new application for applicant:", applicant.id);
            
            const newApplicationId = await handleCreateApplication(applicant);

            if (newApplicationId) {
                console.log("Successfully created application:", newApplicationId);
                // Navigate to the application page with the new applicationId using React Router
                navigate(`/application?applicationId=${newApplicationId}`);
            } else {
                alert("Failed to create application. Please try again.");
            }
        } catch (error) {
            console.error("Error creating application:", error);
            alert("Failed to create application. Please try again.");
        } finally {
            setCreatingApplication(false);
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>{error}</div>;
    }

    return (
        <React.Fragment>
            {!existingApplication ? (
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <h4 className="card-title mb-0">Applications</h4>
                                {/* Temporarily commented - Registration closed for 2025/26 */}
                                {/* {isProfileComplete && !has2025Application ? (
                                    <Button
                                        className="btn btn-primary"
                                        onClick={handleCreateNewApplication}
                                        disabled={creatingApplication}
                                    >
                                        {creatingApplication ? (
                                            <>
                                                <i className="fas fa-spinner fa-spin me-1"></i>
                                                Creating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-plus me-1"></i>
                                                Start 2025/26 Application
                                            </>
                                        )}
                                    </Button>
                                ) : (
                                    <div className="text-muted">
                                        <small>
                                            {!isProfileComplete 
                                                ? 'Complete your profile to start a new application'
                                                : has2025Application 
                                                ? '2025/26 application already exists'
                                                : 'Complete your profile to start a new application'
                                            }
                                        </small>
                                    </div>
                                )} */}
                            </CardHeader>
                            <CardBody>
                                {!isProfileComplete ? (
                                    <div className="text-center py-5">
                                        <div className="mb-3">
                                            <i className="fas fa-user-edit display-4 text-muted"></i>
                                        </div>
                                        <h5 className="text-muted">Complete Your Profile First</h5>
                                        <p className="text-muted mb-0">
                                            Please complete all mandatory fields in your personal profile before starting a new application.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="text-center py-5">
                                        <div className="mb-3">
                                            <i className="fas fa-file-alt display-4 text-muted"></i>
                                        </div>
                                        <h5 className="text-muted">No Applications Found</h5>
                                        <p className="text-muted mb-0">
                                            {has2025Application 
                                                ? 'You have already submitted your 2025/26 application.'
                                                : 'You haven\'t started any applications yet. Click "Start 2025/26 Application" to begin.'
                                            }
                                        </p>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            ) : (
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <h4 className="card-title mb-0">Applications</h4>
                                {/* Temporarily commented - Registration closed for 2025/26 */}
                                {/* {isProfileComplete && !has2025Application && (
                                    <Button
                                        className="btn btn-primary"
                                        onClick={handleCreateNewApplication}
                                        disabled={creatingApplication}
                                    >
                                        {creatingApplication ? (
                                            <>
                                                <i className="fas fa-spinner fa-spin me-1"></i>
                                                Creating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-plus me-1"></i>
                                                Start 2025/26 Application
                                            </>
                                        )}
                                    </Button>
                                )} */}
                            </CardHeader>
                            <CardBody>
                                {/* Desktop/Table view */}
                                <div className="d-none d-md-block">
                                    <div id="applicationList">
                                        <div className="table-responsive table-card mt-3 mb-1">
                                            <table className="table align-middle table-nowrap" id="customerTable" style={{ minWidth: '1000px' }}>
                                                <thead className="table-light">
                                                    <tr>
                                                        <th className="sort" data-sort="application_number">Application#</th>
                                                        <th className="sort" data-sort="name">Name</th>
                                                        <th className="sort" data-sort="status">Status</th>
                                                        <th className="sort" data-sort="aid_needed">Aid Needed</th>
                                                        <th className="sort" data-sort="remarks">Remarks</th>
                                                        {/* <th className="sort" data-sort="aid_granted">Aid Granted</th> */}
                                                        <th className="sort" data-sort="action">Action</th>
                                                        <th className="sort" data-sort="pdf">PDF</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="list form-check-all">
                                                    {applications.map((app) => {
                                                        const remarks = processApplicationRemarks(app);
                                                        return (
                                                            <tr key={app.id} className={remarks.length > 0 ? 'table-warning' : ''}>
                                                                <td>
                                                                    {app.applicationNumber}
                                                                    {remarks.length > 0 && (
                                                                        <i className="fas fa-exclamation-triangle text-warning ms-2" 
                                                                           title={`${remarks.length} remark(s) require attention`}></i>
                                                                    )}
                                                                </td>
                                                                <td>{app.mMApplicantName}</td>
                                                                <td>
                                                                    <span className={`badge ${getStatusDisplay(app.status).badgeClass}`}>
                                                                        <i className={`${getStatusDisplay(app.status).icon} me-1`}></i>
                                                                        {getDisplayStatus(app.status)}
                                                                    </span>
                                                                </td>
                                                                <td>₹ {app.aidNeeded ? parseFloat(app.aidNeeded).toLocaleString('en-IN') : '0'}</td>
                                                                <td>
                                                                    <RemarksDisplay remarks={remarks} applicationId={app.id} />
                                                                </td>
                                                                {/* <td>₹ {app.aidGranted ? parseFloat(app.aidGranted).toLocaleString('en-IN') : '-'}</td> */}
                                                                <td>
                                                                    <div className="d-flex gap-2">
                                                                        {/* Show either Continue/Resume OR View button, not both */}
                                                                        {getStatusPermissions(app.status).canEdit ? (
                                                                            <div className="edit">
                                                                                <Link to={`/application?applicationId=${app.id}`} className="btn btn-sm btn-primary edit-item-btn">
                                                                                    <i className="fas fa-edit me-1"></i>
                                                                                    {app.status === APPLICATION_STATUS.MISSING_INFORMATION ? 'Resume' : 'Continue'}
                                                                                </Link>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="view">
                                                                                <Link to={`/application?applicationId=${app.id}&viewOnly=true`} className="btn btn-sm btn-info view-item-btn">
                                                                                    <i className="fas fa-eye me-1"></i>View
                                                                                </Link>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <ApplicationPDFHandler
                                                                        applicationId={app.id}
                                                                        applicantName={app.mMApplicantName}
                                                                        status={app.status}
                                                                        applicationNumber={app.applicationNumber}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                {/* Mobile view */}
                                <div className="d-md-none">
                                    <div className="row">
                                        {applications.map((app) => {
                                            const remarks = processApplicationRemarks(app);
                                            return (
                                                <div key={app.id} className="col-12 mb-3">
                                                    <Card className={remarks.length > 0 ? 'border-warning' : ''} 
                                                          style={remarks.length > 0 ? { borderWidth: '2px', backgroundColor: '#fff9e6' } : {}}>
                                                        <CardBody>
                                                            <CardTitle tag="h5">
                                                                Application #{app.applicationNumber}
                                                                {remarks.length > 0 && (
                                                                    <i className="fas fa-exclamation-triangle text-warning ms-2" 
                                                                       title={`${remarks.length} remark(s) require attention`}></i>
                                                                )}
                                                            </CardTitle>
                                                            <p><strong>Name:</strong> {app.mMApplicantName}</p>
                                                            <p>
                                                                <strong>Status:</strong>{' '}
                                                                <span className={`badge ${getStatusDisplay(app.status).badgeClass}`}>
                                                                    <i className={`${getStatusDisplay(app.status).icon} me-1`}></i>
                                                                    {getDisplayStatus(app.status)}
                                                                </span>
                                                            </p>
                                                            <p><strong>Aid Needed:</strong> ₹ {app.aidNeeded ? parseFloat(app.aidNeeded).toLocaleString('en-IN') : '0'}</p>
                                                            
                                                            {/* Remarks Section for Mobile */}
                                                            {/* Remarks Section for Mobile */}
                                                            <div className="mb-3">
                                                                <strong>
                                                                    <i className="fas fa-exclamation-circle text-warning me-2"></i>
                                                                    Remarks:
                                                                </strong>
                                                                {remarks.length > 0 && (
                                                                    <div className="alert alert-warning mt-2 py-2 px-3" style={{ fontSize: '0.85em' }}>
                                                                        <i className="fas fa-info-circle me-2"></i>
                                                                        <strong>Action Required:</strong> Please review and address the remarks below
                                                                    </div>
                                                                )}
                                                                <div className="mt-1">
                                                                    <RemarksDisplay remarks={remarks} applicationId={app.id} />
                                                                </div>
                                                            </div>
                                                            
                                                            {/* <p><strong>Aid Granted:</strong> ₹ {app.aidGranted ? parseFloat(app.aidGranted).toLocaleString('en-IN') : '-'}</p> */}
                                                            
                                                            <div className="d-flex flex-column gap-2">
                                                                <div className="d-flex gap-2">
                                                                    {/* Show either Continue/Resume OR View button, not both */}
                                                                    {getStatusPermissions(app.status).canEdit ? (
                                                                        <Link to={`/application?applicationId=${app.id}`} className="btn btn-primary btn-sm">
                                                                            <i className="fas fa-edit me-1"></i>
                                                                            {app.status === APPLICATION_STATUS.MISSING_INFORMATION ? 'Resume' : 'Continue'}
                                                                        </Link>
                                                                    ) : (
                                                                        <Link to={`/application?applicationId=${app.id}&viewOnly=true`} className="btn btn-info btn-sm">
                                                                            <i className="fas fa-eye me-1"></i>View
                                                                        </Link>
                                                                    )}
                                                                </div>
                                                                
                                                                {/* PDF Actions for Mobile */}
                                                                <div className="border-top pt-2">
                                                                    <small className="text-muted d-block mb-2">PDF Actions:</small>
                                                                    <ApplicationPDFHandler
                                                                        applicationId={app.id}
                                                                        applicantName={app.mMApplicantName}
                                                                        status={app.status}
                                                                        applicationNumber={app.applicationNumber}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </CardBody>
                                                    </Card>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            )}
        </React.Fragment>
    );
};

export default ApplicationList;
